import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import { useState } from 'react';
import createUpdateInspectorAccount from 'src/api/createUpdateInspectorAccount';
import AccountLoader from 'src/pages/authentication/components/inspectorAccount/AccountLoader';
import iconStyles from 'src/components/css/iconStyle';
import LogoDT from 'src/icons/LogoDT';
import { UserInspectorResource, InspectorStatus } from 'src/types/userAccount';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Link as RouterLink } from 'react-router-dom';
import * as Yup from 'yup';
import { formatDate } from 'src/utils/datetimeHelper';
import ErrorMessage from 'src/pages/authentication/components/ErrorMessage';

interface InspectorFormProps {
  resetPassword: (email: string, expirationDate: string) => React.ReactNode;
}
const InspectorForm = ({ resetPassword }: InspectorFormProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [inspectorState, setInspectorState] =
    useState<UserInspectorResource | null>(null);
  const emailValidation = /^[a-z\d]+(\.[a-z\d]+|_[a-z\d]+)*$/gi;
  const specialCharacters = /[^a-zA-Z\d\s/._]/gi;
  const incorrectStartOrEnd = /^[^a-z\d]|[^a-z\d]$/gi;
  const whiteSpaces = /\s+/g;

  const formik = useFormik({
    initialValues: {
      inspectorEmail: '',
    },
    validationSchema: Yup.object({
      inspectorEmail: Yup.string()
        .required('Debe ingresar su usuario')
        .test({
          name: 'is-inspector-email',
          test(value, context) {
            if (value == undefined) {
              return context.createError({
                message: 'Ingrese el usuario',
              });
            }
            if (whiteSpaces.test(value)) {
              return context.createError({
                message: 'No ingrese espacios en blanco',
              });
            }
            if (specialCharacters.test(value)) {
              return context.createError({
                message: 'No ingrese caracteres especiales',
              });
            }
            if (incorrectStartOrEnd.test(value)) {
              return context.createError({
                message: 'No debe empezar o terminar con caracteres especiales',
              });
            }
            if (!emailValidation.test(value)) {
              return context.createError({
                message: 'El usuario no es válido',
              });
            }
            return true;
          },
        }),
    }),
    onSubmit: ({ inspectorEmail }) => {
      setLoading(true);
      createUpdateInspectorAccount(`${inspectorEmail.toLowerCase()}@dt.gob.cl`)
        .then((res) => {
          if (res.status === 200 || res.status === 201) {
            setInspectorState(res.data);
          } else {
            setInspectorState({
              email: inspectorEmail.toLowerCase(),
              status: InspectorStatus.ERROR,
              expirationDate: null,
            });
          }
        })
        .catch((err) => {
          setInspectorState({
            email: inspectorEmail.toLowerCase(),
            status: InspectorStatus.ERROR,
            expirationDate: null,
          });
          // eslint-disable-next-line no-console
          console.error(err);
        })
        .finally(() => setLoading(false));
    },
  });

  if (loading) {
    return <AccountLoader isInspector />;
  }

  if (inspectorState?.status === InspectorStatus.ERROR) {
    return (
      <ErrorMessage errorMessage="Ha ocurrido un error inesperado. Por favor intente nuevamente.">
        <Button
          id="retry-button"
          color="primary"
          fullWidth
          size="large"
          type="button"
          variant="contained"
          onClick={formik.handleChange}
          sx={{ marginTop: '16px' }}
        >
          Reintentar
        </Button>
      </ErrorMessage>
    );
  }

  if (
    inspectorState?.status === InspectorStatus.SUCCESSFULLY_ACTIVATED &&
    inspectorState.email !== undefined
  ) {
    return (
      <>
        <Typography
          color="textPrimary"
          variant="h4"
          marginBottom={3}
        >
          ¡Cuenta Activada!
        </Typography>
        <CheckCircleRoundedIcon
          color="primary"
          style={iconStyles}
        />
        <Typography>
          <Typography
            color="textPrimary"
            variant="body1"
            marginTop={3}
            marginX="auto"
          >
            {`Su contraseña ha sido enviada a ${inspectorState.email.toLowerCase()}`}
          </Typography>
          <Typography
            color="textPrimary"
            variant="body1"
            marginTop={2}
            marginX="auto"
            sx={{ width: '100%' }}
          >
            Esta expirará automáticamente el día
            <span
              style={{
                fontWeight: 500,
                display: 'inline-block',
              }}
            >
              {inspectorState.expirationDate !== null
                ? formatDate(inspectorState.expirationDate)
                : ''}
            </span>
          </Typography>
        </Typography>
        <Button
          id="login-button"
          component={RouterLink}
          to="/loginMailInspector"
          color="primary"
          fullWidth
          size="large"
          type="button"
          variant="contained"
          sx={{ marginTop: 2 }}
        >
          Iniciar Sesión
        </Button>
      </>
    );
  }

  if (
    inspectorState?.status === InspectorStatus.ALREADY_ACTIVATED &&
    resetPassword
  ) {
    return resetPassword(
      inspectorState.email ?? '',
      inspectorState.expirationDate === null
        ? ''
        : formatDate(inspectorState.expirationDate)
    );
  }

  return (
    <form
      id="inspector-form"
      onSubmit={formik.handleSubmit}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
      }}
      noValidate
      autoComplete="off"
    >
      <Typography
        color="textPrimary"
        variant="h4"
        marginBottom={3}
      >
        Acceso Inspector
      </Typography>
      <LogoDT style={iconStyles} />
      <TextField
        id="inspector-input"
        autoFocus
        fullWidth
        label="Usuario"
        margin="normal"
        name="inspectorEmail"
        onChange={formik.handleChange}
        type="text"
        variant="outlined"
        autoComplete="off"
        value={formik.values.inspectorEmail}
        InputProps={{
          endAdornment: <Typography>@dt.gob.cl</Typography>,
        }}
      />
      {formik.touched.inspectorEmail && formik.errors.inspectorEmail && (
        <Typography
          color="error"
          id="inspector-input-error"
          variant="caption"
          marginBottom={3}
          sx={{
            position: 'absolute',
            left: '5px',
            top: '75px',
          }}
        >
          {formik.errors.inspectorEmail}
        </Typography>
      )}
      <Button
        id="submit-button"
        color="primary"
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        sx={{
          marginTop: '18px',
        }}
      >
        Enviar Contraseña
      </Button>
    </form>
  );
};
export default InspectorForm;
